import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Card,
  SimpleGrid,
  Table,
  Badge,
  List,
  ThemeIcon,
  Divider,
  Box,
  Alert,
  Accordion,
} from '@mantine/core';
import {
  IconChartLine,
  IconClock,
  IconSunrise,
  IconHome,
  IconTemperature,
  IconSolarPanel,
  IconChargingPile,
  IconBrain,
  IconCheck,
  IconInfoCircle,
  IconCalculator,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { Layout } from '../shared/components/Layout';
import { useSEO } from '../shared/hooks';
import { useOSD } from '../contexts/OSDContext';
import { getOSDMetadata, getDefaultOSD } from '../utils/osdMetadata';

export function ConsumptionPatterns() {
  useSEO({
    title: 'Wzorce zużycia energii',
    description: 'Poznaj wzorce zużycia energii i dopasuj taryfę do swojego stylu życia. Optymalne taryfy dla różnych typów gospodarstw.',
  });
  const { currentOSD, getOSDPath } = useOSD();

  // Get OSD metadata for dynamic content
  const osd = currentOSD || getDefaultOSD();
  const osdMetadata = getOSDMetadata(osd);

  return (
    <Layout isAuthenticated={false}>
      {/* Hero Section */}
      <Box style={{ backgroundColor: 'var(--mantine-color-blue-0)', padding: '3rem 0' }}>
        <Container size="xl">
          <Stack gap="md" align="center" ta="center">
            <ThemeIcon size={80} radius="xl" variant="light" color="blue">
              <IconChartLine size={48} />
            </ThemeIcon>
            <Title order={1} size={42} fw={700}>
              Wzorce zużycia energii elektrycznej
            </Title>
            <Text size="lg" c="dimmed" maw={800}>
              Dowiedz się, czym jest wzorzec zużycia energii i jak wpływa na koszty Twojego
              rachunku za prąd. Poznaj 7 gotowych wzorców i wybierz ten, który najlepiej
              odzwierciedla Twoje przyzwyczajenia.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Legal Disclaimer - Critical for compliance */}
      <Container size="xl" py={40}>
        <Alert
          variant="light"
          color="orange"
          title="⚠️ Ważne informacje prawne"
          icon={<IconAlertTriangle />}
        >
          <Stack gap="sm">
            <Text size="sm">
              <strong>Charakter informacji:</strong> Informacje na tej stronie mają charakter{' '}
              <strong>edukacyjny i informacyjny</strong>. Wzorce zużycia są matematycznymi modelami,
              a nie rzeczywistym pomiarem Twojego zużycia energii.
            </Text>
            <Text size="sm">
              <strong>Nie stanowi porady:</strong> Informacje nie stanowią porady finansowej ani
              rekomendacji wyboru taryfy. Rzeczywiste oszczędności zależą od wielu czynników i mogą
              być niższe lub wyższe niż podane przykłady.
            </Text>
            <Text size="sm">
              <strong>Zmienność cen:</strong> Wszystkie przykłady kosztów oparte są na cenach z
              roku 2026 (ceny rynkowe zatwierdzone przez URE). Ceny mogą ulec zmianie w przyszłości.
            </Text>
          </Stack>
        </Alert>
      </Container>

      {/* Introduction - What is consumption pattern */}
      <Container size="xl" py={60}>
        <Alert icon={<IconInfoCircle />} color="blue" variant="light" mb="xl">
          <Stack gap="sm">
            <Text size="sm" fw={700}>
              Co to jest wzorzec zużycia energii?
            </Text>
            <Text size="sm" c="dimmed">
              Wzorzec zużycia to matematyczny model, który pokazuje, w jaki sposób używasz energii
              elektrycznej w ciągu doby. Określa, o których godzinach zużywasz najwięcej prądu,
              a kiedy najmniej.
            </Text>
            <Text size="sm" c="dimmed">
              <strong>Dlaczego to ważne:</strong> Kalkulator używa wzorca do obliczenia,
              ile płacisz za prąd w różnych taryfach strefowych (G12, G12w, G13). Dobry wzorzec
              pomaga dokładniej oszacować koszty.
            </Text>
            <Text size="sm" c="dimmed">
              <strong>Przykład:</strong> Jeśli najwięcej energii zużywasz wieczorem (18-22), taryfa
              G12 lub G12w z tanią nocą może być dla Ciebie korzystniejsza niż taryfa G11.
            </Text>
          </Stack>
        </Alert>
      </Container>

      {/* Quick Comparison Table */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Porównanie wzorców - szybki przegląd
            </Title>
            <Text c="dimmed" mb="xl">
              7 gotowych wzorców zużycia energii dostępnych w kalkulatorze
            </Text>
          </div>

          <Table.ScrollContainer minWidth={800}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Wzorzec</Table.Th>
                  <Table.Th>Opis</Table.Th>
                  <Table.Th>Kiedy najwięcej prądu</Table.Th>
                  <Table.Th>Dla kogo</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td>
                    <Badge color="blue" size="lg" variant="filled">
                      Równomierne 24h
                    </Badge>
                  </Table.Td>
                  <Table.Td>Stałe zużycie przez całą dobę</Table.Td>
                  <Table.Td>Brak różnic między godzinami</Table.Td>
                  <Table.Td>Standardowe gospodarstwa, emeryci</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Td>
                    <Badge color="orange" size="lg" variant="filled">
                      Poranki i wieczory
                    </Badge>
                  </Table.Td>
                  <Table.Td>Najwięcej rano i wieczorem</Table.Td>
                  <Table.Td>Rano: 6-9, Wieczór: 18-22</Table.Td>
                  <Table.Td>Pracujący poza domem</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Td>
                    <Badge color="grape" size="lg" variant="filled">
                      Praca w domu
                    </Badge>
                  </Table.Td>
                  <Table.Td>Wysokie zużycie przez cały dzień</Table.Td>
                  <Table.Td>Cały dzień: 9-17</Table.Td>
                  <Table.Td>Home office, freelancerzy</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Td>
                    <Badge color="red" size="lg" variant="filled">
                      Z pompą ciepła
                    </Badge>
                  </Table.Td>
                  <Table.Td>Zwiększone rano i wieczorem</Table.Td>
                  <Table.Td>Rano: 6-9, Wieczór: 17-22</Table.Td>
                  <Table.Td>Pompa ciepła do ogrzewania/chłodzenia</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Td>
                    <Badge color="yellow" size="lg" variant="filled">
                      Z fotowoltaiką
                    </Badge>
                  </Table.Td>
                  <Table.Td>Mało w dzień, dużo rano/wieczorem</Table.Td>
                  <Table.Td>Rano: 6-8, Wieczór: 18-23</Table.Td>
                  <Table.Td>Właściciele paneli słonecznych</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Td>
                    <Badge color="cyan" size="lg" variant="filled">
                      Z samochodem elektrycznym
                    </Badge>
                  </Table.Td>
                  <Table.Td>Nocne ładowanie samochodu</Table.Td>
                  <Table.Td>Noc: 22-6 (ładowanie)</Table.Td>
                  <Table.Td>Właściciele samochodów elektrycznych</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Td>
                    <Badge color="green" size="lg" variant="filled">
                      Dostosowany
                    </Badge>
                  </Table.Td>
                  <Table.Td>Automatycznie dopasowany do Twoich danych</Table.Td>
                  <Table.Td>Zależy od Twoich urządzeń</Table.Td>
                  <Table.Td>Wszyscy - najbardziej precyzyjny!</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Stack>
      </Container>

      {/* Detailed Pattern Descriptions */}
      <Box style={{ backgroundColor: 'var(--mantine-color-gray-0)' }} py={60}>
        <Container size="xl">
          <Stack gap="xl">
            <div>
              <Title order={2} mb="md">
                Szczegółowe opisy wzorców
              </Title>
              <Text c="dimmed">
                Poznaj każdy wzorzec zużycia energii i dowiedz się, który pasuje do Twojego stylu życia
              </Text>
            </div>

            {/* Pattern 1: Uniform (Równomierne 24h) */}
            <Card shadow="sm" padding="xl" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon size={50} radius="md" variant="light" color="blue">
                    <IconClock size={28} />
                  </ThemeIcon>
                  <div>
                    <Badge color="blue" size="lg" variant="filled" mb="xs">
                      Równomierne 24h
                    </Badge>
                    <Title order={3}>Wzorzec równomierny</Title>
                  </div>
                </Group>
              </Group>

              <Text size="lg" mb="lg">
                Jednolite zużycie energii przez całą dobę. Każda godzina ma taką samą wartość -
                używasz tyle samo prądu o 3:00 w nocy, co o 18:00 wieczorem.
              </Text>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <div>
                  <Text fw={600} mb="sm">
                    Dla kogo:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="blue" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Standardowe gospodarstwa domowe</List.Item>
                    <List.Item>Osoby starsze i emeryci (przebywają w domu cały dzień)</List.Item>
                    <List.Item>Małe mieszkania bez dużych urządzeń</List.Item>
                    <List.Item>Osoby, które nie planują zużycia energii</List.Item>
                  </List>
                </div>

                <div>
                  <Text fw={600} mb="sm" c="green">
                    Zalety:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="green" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Najprostszy do zrozumienia</List.Item>
                    <List.Item>Brak konieczności planowania, kiedy włączać urządzenia</List.Item>
                    <List.Item>Idealne dla taryfy G11 (jedna cena przez całą dobę)</List.Item>
                    <List.Item>Przewidywalne koszty</List.Item>
                  </List>
                </div>
              </SimpleGrid>

              <Divider my="lg" />

              <Alert icon={<IconInfoCircle />} color="blue" variant="light">
                <Text size="sm">
                  <strong>Uwaga:</strong> To domyślny wzorzec w kalkulatorze, jeśli nie wybierzesz
                  żadnego innego. Jest najprostszy, ale może nie odzwierciedlać rzeczywistości -
                  większość gospodarstw zużywa więcej energii rano i wieczorem.
                </Text>
              </Alert>
            </Card>

            {/* Pattern 2: Mornings & Evenings */}
            <Card shadow="sm" padding="xl" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon size={50} radius="md" variant="light" color="orange">
                    <IconSunrise size={28} />
                  </ThemeIcon>
                  <div>
                    <Badge color="orange" size="lg" variant="filled" mb="xs">
                      Poranki i wieczory
                    </Badge>
                    <Title order={3}>Wzorzec poranny i wieczorny</Title>
                  </div>
                </Group>
              </Group>

              <Text size="lg" mb="lg">
                Najwięcej energii zużywasz rano (6-9) przed wyjściem do pracy/szkoły i wieczorem
                (18-22) po powrocie do domu. W nocy i w południe zużycie jest niskie.
              </Text>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <div>
                  <Text fw={600} mb="sm">
                    Dla kogo:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="orange" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Osoby pracujące poza domem (8-17)</List.Item>
                    <List.Item>Rodziny z dziećmi w szkole</List.Item>
                    <List.Item>Wszyscy, którzy rano wychodzą i wieczorem wracają</List.Item>
                  </List>
                </div>

                <div>
                  <Text fw={600} mb="sm" c="green">
                    Zalety:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="green" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Naturalny wzorzec dla większości Polaków</List.Item>
                    <List.Item>Pasuje do taryf G12/G12w (tańsza energia w nocy)</List.Item>
                    <List.Item>Możliwość oszczędzania przez pralką/zmywarkę w nocy</List.Item>
                  </List>
                </div>
              </SimpleGrid>

              <Divider my="lg" />

              <Alert icon={<IconInfoCircle />} color="orange" variant="light">
                <Text size="sm">
                  <strong>Najpopularniejszy wzorzec w Polsce!</strong> Idealnie pasuje do taryfy
                  G12w, gdzie całe weekendy są tanie przez 24 godziny.
                </Text>
              </Alert>
            </Card>

            {/* Pattern 3: Work from Home */}
            <Card shadow="sm" padding="xl" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon size={50} radius="md" variant="light" color="grape">
                    <IconHome size={28} />
                  </ThemeIcon>
                  <div>
                    <Badge color="grape" size="lg" variant="filled" mb="xs">
                      Praca w domu
                    </Badge>
                    <Title order={3}>Wzorzec home office</Title>
                  </div>
                </Group>
              </Group>

              <Text size="lg" mb="lg">
                Wysokie zużycie energii przez cały dzień (9-17) z powodu pracy zdalnej. Pracujesz
                przy komputerze, używasz oświetlenia, włączasz ogrzewanie lub klimatyzację.
              </Text>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <div>
                  <Text fw={600} mb="sm">
                    Dla kogo:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="grape" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Osoby pracujące zdalnie (home office)</List.Item>
                    <List.Item>Freelancerzy i samozatrudnieni</List.Item>
                    <List.Item>Osoby przebywające w domu przez większość dnia</List.Item>
                    <List.Item>Rodzice na urlopie macierzyńskim/ojcowskim</List.Item>
                  </List>
                </div>

                <div>
                  <Text fw={600} mb="sm" c="green">
                    Zalety:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="green" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Realistyczny dla osób pracujących w domu</List.Item>
                    <List.Item>Uwzględnia zużycie komputera przez 8 godzin</List.Item>
                    <List.Item>Pasuje do taryfy G12 (tania strefa 13-15)</List.Item>
                  </List>
                </div>
              </SimpleGrid>

              <Divider my="lg" />

              <Alert icon={<IconInfoCircle />} color="grape" variant="light">
                <Text size="sm">
                  Jeśli pracujesz w domu, taryfa G12 może być dobra - od 13:00 do 15:00 masz taniej
                  prąd! W tym czasie możesz uruchomić pralkę, zmywarkę lub naładować urządzenia.
                </Text>
              </Alert>
            </Card>

            {/* Pattern 4: Heat Pump */}
            <Card shadow="sm" padding="xl" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon size={50} radius="md" variant="light" color="red">
                    <IconTemperature size={28} />
                  </ThemeIcon>
                  <div>
                    <Badge color="red" size="lg" variant="filled" mb="xs">
                      Z pompą ciepła
                    </Badge>
                    <Title order={3}>Wzorzec z pompą ciepła</Title>
                  </div>
                </Group>
              </Group>

              <Text size="lg" mb="lg">
                Znacznie zwiększone zużycie energii rano (6-9) i wieczorem (17-22) przez pompę ciepła,
                która ogrzewa dom przed wyjściem i po powrocie.
              </Text>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <div>
                  <Text fw={600} mb="sm">
                    Dla kogo:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="red" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Gospodarstwa z pompą ciepła do ogrzewania</List.Item>
                    <List.Item>Gospodarstwa z pompą ciepła do chłodzenia (klimatyzacja)</List.Item>
                    <List.Item>Elektryczne ogrzewanie podłogowe</List.Item>
                  </List>
                </div>

                <div>
                  <Text fw={600} mb="sm" c="green">
                    Zalety:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="green" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Realistyczny dla domów z pompą ciepła</List.Item>
                    <List.Item>Pomaga wybrać optymalną taryfę (G12w lub G13)</List.Item>
                    <List.Item>Uwzględnia cykle grzewcze pompy ciepła</List.Item>
                  </List>
                </div>
              </SimpleGrid>

              <Divider my="lg" />

              <Alert icon={<IconInfoCircle />} color="red" variant="light">
                <Text size="sm">
                  Pompy ciepła najintensywniej pracują rano (przed wyjściem) i wieczorem (po
                  powrocie). Taryfa G12w z tanią nocą i weekendami może przynieść oszczędności -
                  ustaw termostaty tak, żeby pompa intensywniej pracowała w tanich strefach!
                </Text>
              </Alert>
            </Card>

            {/* Pattern 5: Solar Panels */}
            <Card shadow="sm" padding="xl" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon size={50} radius="md" variant="light" color="yellow">
                    <IconSolarPanel size={28} />
                  </ThemeIcon>
                  <div>
                    <Badge color="yellow" size="lg" variant="filled" mb="xs">
                      Z fotowoltaiką
                    </Badge>
                    <Title order={3}>Wzorzec z panelami słonecznymi</Title>
                  </div>
                </Group>
              </Group>

              <Text size="lg" mb="lg">
                Twoje panele słoneczne produkują energię w dzień (9-17), więc prawie nie pobierasz
                prądu z sieci. Ale rano (przed wschodem słońca) i wieczorem (po zachodzie) pobierasz
                dużo energii z sieci.
              </Text>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <div>
                  <Text fw={600} mb="sm">
                    Dla kogo:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="yellow" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Właściciele instalacji fotowoltaicznej (panele słoneczne)</List.Item>
                    <List.Item>Prosumenci (produkujesz własną energię)</List.Item>
                    <List.Item>Chcący zoptymalizować bilans energetyczny</List.Item>
                  </List>
                </div>

                <div>
                  <Text fw={600} mb="sm" c="green">
                    Zalety:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="green" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Realistyczny rozkład dla prosumentów</List.Item>
                    <List.Item>Pokazuje, kiedy faktycznie pobierasz energię z sieci</List.Item>
                    <List.Item>Pomaga oszacować oszczędności z fotowoltaiki</List.Item>
                  </List>
                </div>
              </SimpleGrid>

              <Divider my="lg" />

              <Alert icon={<IconAlertTriangle />} color="yellow" variant="light">
                <Text size="sm">
                  <strong>Uwaga!</strong> Taryfa G12 może NIE być opłacalna z fotowoltaiką, bo tania
                  strefa (13-15) pokrywa się z produkcją słoneczną - i tak wtedy nie bierzesz prądu
                  z sieci. Lepiej wybierz G11 lub G12w! Ten model zakłada średnią roczną produkcję -
                  faktyczna produkcja jest sezonowa (zima: 20-40%, lato: 100-120% średniej).
                </Text>
              </Alert>
            </Card>

            {/* Pattern 6: Electric Vehicle */}
            <Card shadow="sm" padding="xl" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon size={50} radius="md" variant="light" color="cyan">
                    <IconChargingPile size={28} />
                  </ThemeIcon>
                  <div>
                    <Badge color="cyan" size="lg" variant="filled" mb="xs">
                      Z samochodem elektrycznym
                    </Badge>
                    <Title order={3}>Wzorzec z EV (ładowanie nocne)</Title>
                  </div>
                </Group>
              </Group>

              <Text size="lg" mb="lg">
                Znacznie zwiększone zużycie energii w nocy (22-6) przez ładowanie samochodu
                elektrycznego. Normalnie zużycie w dzień, bardzo wysokie w nocy.
              </Text>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <div>
                  <Text fw={600} mb="sm">
                    Dla kogo:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="cyan" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Właściciele samochodów elektrycznych</List.Item>
                    <List.Item>Osoby ładujące samochód w domu w nocy</List.Item>
                    <List.Item>Chcący maksymalnie obniżyć koszty ładowania</List.Item>
                  </List>
                </div>

                <div>
                  <Text fw={600} mb="sm" c="green">
                    Zalety:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="green" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Uwzględnia realne zużycie energii na ładowanie EV</List.Item>
                    <List.Item>Idealnie pasuje do taryf G12/G12w (tańsza noc!)</List.Item>
                    <List.Item>Maksymalizuje oszczędności na ładowaniu</List.Item>
                  </List>
                </div>
              </SimpleGrid>

              <Divider my="lg" />

              <Alert icon={<IconInfoCircle />} color="cyan" variant="light">
                <Stack gap="xs">
                  <Text size="sm">
                    Ładowanie samochodu elektrycznego w nocy (22-6) w taryfie G12 lub G12w może
                    <strong> potencjalnie</strong> przynieść oszczędności rzędu <strong>około 28-29%</strong> na
                    kosztach ładowania w porównaniu do taryfy G11, przy założeniu stałego zużycia nocnego
                    i niezmiennych cenach energii.
                  </Text>
                  <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                    Źródło kalkulacji: Taryfy {osdMetadata.shortName} 2026 zatwierdzone przez URE (G11: ~1,13 zł/kWh,
                    G12 noc: ~0,82 zł/kWh, G12w noc: ~0,80 zł/kWh). Oszczędność: (1,13-0,82)/1,13 ≈ 27%.
                  </Text>
                  <Text size="sm" fw={600} mt="xs">
                    Przykładowe ładowanie: 10-15 kWh na noc (około 50 km zasięgu)
                  </Text>
                  <Text size="xs" c="dimmed">
                    UWAGA: Rzeczywiste zużycie zależy od modelu samochodu: Mały miejski EV: 12-15
                    kWh/100km, Średni EV: 15-20 kWh/100km, Duży SUV EV: 20-30 kWh/100km. Przed
                    wyborem taryfy sprawdź rzeczywiste zużycie swojego samochodu.
                  </Text>
                </Stack>
              </Alert>
            </Card>

            {/* Pattern 7: Intelligent/Suggested (Dostosowany) */}
            <Card shadow="sm" padding="xl" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon size={50} radius="md" variant="light" color="green">
                    <IconBrain size={28} />
                  </ThemeIcon>
                  <div>
                    <Badge color="green" size="lg" variant="filled" mb="xs">
                      Dostosowany
                    </Badge>
                    <Title order={3}>Wzorzec automatycznie dostosowany</Title>
                  </div>
                </Group>
              </Group>

              <Text size="lg" mb="lg">
                Automatycznie dopasowany wzorzec stworzony specjalnie dla Twojego gospodarstwa
                na podstawie odpowiedzi z{' '}
                <Link to={getOSDPath('/kalkulatory/podstawowy')} style={{ color: 'var(--mantine-color-blue-6)', textDecoration: 'underline' }}>
                  Kroku 2 kalkulatora
                </Link>
                .
              </Text>

              <Stack gap="md">
                <div>
                  <Text fw={600} mb="sm">
                    Jak działa:
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={
                      <ThemeIcon color="green" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>
                      Zaczyna od bazowego wzorca "Poranki i wieczory" (typowy dla większości
                      gospodarstw)
                    </List.Item>
                    <List.Item>
                      Analizuje Twoje odpowiedzi z Kroku 2 (wielkość gospodarstwa, pompa ciepła,
                      fotowoltaika, samochód elektryczny)
                    </List.Item>
                    <List.Item>
                      <strong>Pompa ciepła</strong> → zwiększa zużycie rano (6-8) i wieczorem
                      (17-22) o 50%
                    </List.Item>
                    <List.Item>
                      <strong>Fotowoltaika</strong> → zmniejsza zużycie w dzień (9-17) o 40% (bo
                      produkujesz własny prąd)
                    </List.Item>
                    <List.Item>
                      <strong>Samochód elektryczny</strong> → zwiększa zużycie w nocy (22-6) o 80%
                      (ładowanie)
                    </List.Item>
                    <List.Item>
                      <strong>Wielkość gospodarstwa</strong> → większe gospodarstwo = wyższe zużycie
                      wieczorem
                    </List.Item>
                    <List.Item>Normalizuje wzorzec do 100% (suma wszystkich godzin = 100%)</List.Item>
                  </List>
                </div>
              </Stack>

              <Divider my="lg" />

              <Alert icon={<IconBrain />} color="green" variant="light">
                <Text size="sm">
                  <strong>Najlepsza opcja dla większości użytkowników!</strong> Wzorzec automatycznie
                  dostosowuje się do Twoich odpowiedzi w Kroku 2. Kliknij przycisk "Dostosowany (na
                  podstawie urządzeń)" w Kroku 3 kalkulatora.
                </Text>
              </Alert>

              <Alert icon={<IconInfoCircle />} color="blue" variant="light" mt="md">
                <Text size="sm">
                  <strong>Uwaga techniczna:</strong> Ten wzorzec używa prostych reguł matematycznych
                  (if-then), a nie zaawansowanej sztucznej inteligencji. To model upraszczający
                  rzeczywistość, a nie pomiar rzeczywistego zużycia.
                </Text>
              </Alert>
            </Card>
          </Stack>
        </Container>
      </Box>

      {/* How patterns affect costs */}
      <Container size="xl" py={60}>
        <Title order={2} mb="md">
          Jak wzorce wpływają na koszty?
        </Title>

        <Alert icon={<IconInfoCircle />} color="blue" variant="light" mb="xl">
          <Stack gap="sm">
            <Text size="sm">
              Wzorzec zużycia energii określa, <strong>o której godzinie</strong> zużywasz najwięcej
              prądu. To ma znaczenie dla taryf strefowych (G12, G12w, G13), gdzie prąd w różnych
              godzinach ma różną cenę.
            </Text>
            <Text size="sm">
              <strong>Przykłady:</strong>
            </Text>
            <List size="sm" spacing="xs">
              <List.Item>
                <strong>Taryfa G11:</strong> Wzorzec nie ma znaczenia - płacisz ~1.13 zł/kWh przez całą dobę
              </List.Item>
              <List.Item>
                <strong>Taryfa G12:</strong> Strefa dzienna (droższa): ~1.17 zł/kWh, strefa nocna
                (tańsza): ~0.82 zł/kWh (taniej o ~30%!)
              </List.Item>
              <List.Item>
                <strong>Taryfa G12w:</strong> Dni robocze: droga (~1.21 zł) vs tania (~0.80 zł),
                Weekendy i święta: ~0.80 zł przez całą dobę (24h!)
              </List.Item>
            </List>
            <Text size="sm" fw={600} mt="sm">
              Wzorzec określa, ile kWh przypada na strefę dzienną vs nocną → wpływa na całkowity
              koszt rachunku.
            </Text>
          </Stack>
        </Alert>
      </Container>

      {/* Example calculation */}
      <Box style={{ backgroundColor: 'var(--mantine-color-yellow-0)' }} py={60}>
        <Container size="xl">
          <Title order={2} mb="md">
            Przykład obliczenia (dla taryfy G12)
          </Title>

          <Card shadow="md" padding="lg" radius="md" withBorder mb="xl">
            <Stack gap="md">
              <div>
                <Text fw={600}>Gospodarstwo: 4 osoby</Text>
                <Text fw={600}>Zużycie: 3000 kWh/rok (250 kWh/miesiąc)</Text>
                <Text fw={600}>Porównanie: Taryfa G12</Text>
              </div>

              <Divider />

              <div>
                <Badge color="orange" size="lg" mb="sm">
                  Wzorzec: PORANKI I WIECZORY
                </Badge>
                <Text size="sm" c="dimmed" mb="xs">
                  (realistyczny - najwięcej rano i wieczorem)
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>Strefa dzienna (droga): 60% = 150 kWh × 1.17 zł = 175.50 zł</List.Item>
                  <List.Item>Strefa nocna (tania): 40% = 100 kWh × 0.82 zł = 82.00 zł</List.Item>
                </List>
                <Text size="lg" fw={700} mt="sm">
                  Suma G12: 257.50 zł/miesiąc = 3090 zł/rok
                </Text>
              </div>

              <Divider />

              <div>
                <Badge color="blue" size="lg" mb="sm">
                  Wzorzec: RÓWNOMIERNE 24H
                </Badge>
                <Text size="sm" c="dimmed" mb="xs">
                  (nierealny - tyle samo prądu o 3:00 co o 18:00)
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>Strefa dzienna: 70% = 175 kWh × 1.17 zł = 204.75 zł</List.Item>
                  <List.Item>Strefa nocna: 30% = 75 kWh × 0.82 zł = 61.50 zł</List.Item>
                </List>
                <Text size="lg" fw={700} mt="sm">
                  Suma G12: 266.25 zł/miesiąc = 3195 zł/rok
                </Text>
              </div>

              <Divider />

              <Alert icon={<IconAlertTriangle />} color="orange" variant="light">
                <Text size="sm" fw={600}>
                  Różnica między wzorcami: 8.75 zł/miesiąc = 105 zł/rok
                </Text>
                <Text size="sm" mt="xs">
                  Wniosek: Dobry wzorzec = lepsze oszacowanie kosztów! Ale pamiętaj, że to wciąż
                  <strong> szacunki</strong>, nie gwarancje. Rzeczywiste koszty mogą się różnić
                  o ±20-30%.
                </Text>
              </Alert>
            </Stack>
          </Card>

          <Alert icon={<IconAlertTriangle />} color="yellow" variant="filled">
            <Text size="sm" c="dark">
              <strong>Zastrzeżenie prawne:</strong> Powyższy przykład ma charakter ilustracyjny.
              Rzeczywiste oszczędności mogą być niższe, wyższe lub zerowe w zależności od:
              rzeczywistego zużycia, przyszłych zmian cen energii, indywidualnych warunków
              umowy z dostawcą, sezonowości, zmian w nawykach.
            </Text>
          </Alert>
        </Container>
      </Box>

      {/* Decision guide - How to choose pattern */}
      <Container size="xl" py={60}>
        <Title order={2} mb="md">
          Jak wybrać wzorzec w kalkulatorze?
        </Title>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Prosty przewodnik:</Text>
            <List type="ordered" spacing="sm">
              <List.Item>
                <strong>Wejdź na kalkulator</strong> → <Link to={getOSDPath('/kalkulatory/podstawowy')}>Przejdź do kalkulatora</Link>
              </List.Item>
              <List.Item>
                <strong>Wypełnij Krok 1:</strong> Wprowadź miesięczne zużycie energii
              </List.Item>
              <List.Item>
                <strong>Wypełnij Krok 2:</strong> Odpowiedz na pytania o gospodarstwo (liczba osób,
                pompa ciepła, fotowoltaika, samochód elektryczny)
              </List.Item>
              <List.Item>
                <strong>W Kroku 3</strong> (Wzorzec godzinowy):
                <List withPadding size="sm" mt="xs">
                  <List.Item>
                    <strong>Najlepszy wybór:</strong> Kliknij przycisk{' '}
                    <strong>"Dostosowany (na podstawie urządzeń)"</strong>
                  </List.Item>
                  <List.Item>
                    Lub wybierz jeden z gotowych wzorców: "Równomierne", "Poranki i wieczory", "Praca w domu"
                  </List.Item>
                  <List.Item>Lub dostosuj ręcznie 24 godziny (zaawansowani użytkownicy)</List.Item>
                </List>
              </List.Item>
            </List>
          </Stack>
        </Card>
      </Container>

      {/* FAQ Section */}
      <Box style={{ backgroundColor: 'var(--mantine-color-gray-0)' }} py={60}>
        <Container size="xl">
          <Title order={2} mb="md">
            Najczęstsze pytania
          </Title>

          <Accordion variant="separated">
            <Accordion.Item value="pattern-change-consumption">
              <Accordion.Control>
                Czy wybrany wzorzec zmienia moje rzeczywiste zużycie energii?
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  <strong>NIE.</strong> Wzorzec to tylko sposób, w jaki kalkulator rozdziela Twoje
                  miesięczne zużycie na godziny. Rzeczywiste zużycie zależy od tego, jak faktycznie
                  używasz urządzeń (włączasz światło, pralki, piekarnik itp.).
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="unknown-pattern">
              <Accordion.Control>Co jeśli nie znam swojego wzorca zużycia?</Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Wybierz wzorzec <strong>"Dostosowany"</strong> - automatycznie dostosuje się do
                  Twoich odpowiedzi z Kroku 2. To najprostsza i najdokładniejsza opcja! Alternatywnie,
                  sprawdź swoje rzeczywiste zużycie w systemie eLicznik Twojego dostawcy energii.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="change-pattern">
              <Accordion.Control>Czy mogę zmienić wzorzec po obliczeniu?</Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  <strong>TAK.</strong> Wróć do Kroku 3, zmień wzorzec i kliknij ponownie "Oblicz i
                  porównaj wszystkie taryfy". Możesz testować różne wzorce i porównywać wyniki.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="most-accurate">
              <Accordion.Control>Który wzorzec jest najbardziej dokładny?</Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Wzorzec <strong>"Dostosowany"</strong> jest najbardziej precyzyjny, bo uwzględnia
                  Twoje odpowiedzi z Kroku 2 (pompa ciepła, fotowoltaika, samochód elektryczny,
                  wielkość gospodarstwa). Jednak pamiętaj - to wciąż model matematyczny, a nie pomiar
                  rzeczywistego zużycia!
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="pattern-all-tariffs">
              <Accordion.Control>Czy wzorzec wpływa na wszystkie taryfy?</Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  <strong>NIE.</strong> Wzorzec ma znaczenie tylko dla taryf strefowych (G12, G12as,
                  G12w, G13, G13s). Dla taryfy G11 wzorzec nie ma znaczenia, bo cena jest stała przez
                  całą dobę (~1.13 zł/kWh w 2026 roku).
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box style={{ backgroundColor: 'var(--mantine-color-blue-6)', color: 'white' }} py={60}>
        <Container size="md">
          <Stack gap="xl" align="center" ta="center">
            <ThemeIcon size={80} radius="xl" variant="white" color="blue">
              <IconCalculator size={48} />
            </ThemeIcon>
            <Title order={2} c="white">
              Sprawdź, który wzorzec pasuje do Ciebie!
            </Title>
            <Text size="lg" c="white" maw={700}>
              Użyj kalkulatora, aby automatycznie dobrać wzorzec zużycia energii
              i porównać koszty wszystkich taryf. Wypełnij Krok 2, a kalkulator sam zasugeruje
              najlepszy wzorzec dla Twojego gospodarstwa!
            </Text>
            <Button
              size="xl"
              variant="white"
              color="blue"
              component={Link}
              to={getOSDPath('/kalkulatory/podstawowy')}
              leftSection={<IconCalculator size={24} />}
            >
              Przejdź do kalkulatora
            </Button>
            <Text size="sm" c="white" style={{ opacity: 0.9 }}>
              Kalkulator automatycznie dobierze wzorzec na podstawie Twoich odpowiedzi w Kroku 2
            </Text>
          </Stack>
        </Container>
      </Box>
    </Layout>
  );
}
